import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";

const TOUR_KEY = (uid) => `leadflow_tour_v1_${uid || "anon"}`;

export const hasSeenTour = (uid) => !!localStorage.getItem(TOUR_KEY(uid));
export const markTourSeen = (uid) => localStorage.setItem(TOUR_KEY(uid), "1");
export const clearTourFlag = (uid) => localStorage.removeItem(TOUR_KEY(uid));

/**
 * Build & start a guided tour. Returns the Tour instance.
 * @param {{ userId?: string, isAdmin?: boolean, onComplete?: () => void }} opts
 */
export function startTour({ userId, isAdmin, onComplete } = {}) {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: "leadflow-tour",
      scrollTo: { behavior: "smooth", block: "center" },
      cancelIcon: { enabled: true },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 8,
    },
  });

  const finish = () => {
    if (userId) markTourSeen(userId);
    onComplete?.();
  };

  tour.on("complete", finish);
  tour.on("cancel", finish);

  const nav = (i, last) => {
    const arr = [];
    if (i > 0) arr.push({ text: "Back", classes: "shepherd-btn-secondary", action: tour.back });
    arr.push({ text: "Skip tour", classes: "shepherd-btn-ghost", action: tour.cancel });
    arr.push({ text: last ? "Got it" : "Next", classes: "shepherd-btn-primary", action: last ? tour.complete : tour.next });
    return arr;
  };

  const steps = [
    {
      id: "welcome",
      title: "Welcome to CERF LeadFlow 👋",
      text: "A quick 60-second tour of the essentials. You can restart it anytime from the Guide page.",
    },
    {
      id: "sidebar",
      title: "Left sidebar",
      text: "Your main navigation lives here — jump between Overview, Leads, Pipeline, Follow-ups, Upload, Analytics and Guide.",
      attachTo: { element: '[data-testid="sidebar"]', on: "right" },
    },
    {
      id: "leads",
      title: "Leads",
      text: "The central lead list. Search, filter, edit inline, or select many rows for bulk actions like status/priority/reassign.",
      attachTo: { element: '[data-testid="nav-leads"]', on: "right" },
    },
    {
      id: "kanban",
      title: "Pipeline (Kanban)",
      text: "Drag cards between stages to update status instantly. Each stage has its own colour so the board is scannable at a glance.",
      attachTo: { element: '[data-testid="nav-kanban"]', on: "right" },
    },
    {
      id: "followups",
      title: "Follow-ups",
      text: "Overdue, Due today and Upcoming — grouped and one-click actionable. Never lose a lead to silence.",
      attachTo: { element: '[data-testid="nav-followups"]', on: "right" },
    },
    {
      id: "upload",
      title: "Bulk import + Undo",
      text: "Upload CSV/Excel with auto field-mapping and duplicate detection. If a batch was wrong, you can undo the whole import from history.",
      attachTo: { element: '[data-testid="nav-upload"]', on: "right" },
    },
    {
      id: "analytics",
      title: "Analytics",
      text: "Trends, cohort funnel, source ROI and member scorecards — filter by Week / Month / Quarter / Year / Custom.",
      attachTo: { element: '[data-testid="nav-analytics"]', on: "right" },
    },
    {
      id: "notifications",
      title: "Notification bell",
      text: "Live count of Overdue + Due Today + Abandoned leads across your scope. Click any item to jump straight to that lead.",
      attachTo: { element: '[data-testid="notification-bell"]', on: "bottom" },
    },
  ];

  if (isAdmin) {
    steps.push({
      id: "members",
      title: "Members (SuperAdmin)",
      text: "Create sales members, regenerate their login tokens, deactivate, or email their daily reminders from here.",
      attachTo: { element: '[data-testid="nav-members"]', on: "right" },
    });
  }

  steps.push({
    id: "guide",
    title: "Guide",
    text: "Full documentation for every feature is in Guide. That's also where you can restart this tour.",
    attachTo: { element: '[data-testid="nav-guide"]', on: "right" },
  });

  steps.forEach((s, i) => {
    const last = i === steps.length - 1;
    tour.addStep({ ...s, buttons: nav(i, last) });
  });

  tour.start();
  return tour;
}
